<template>
  <div class="mt-2 mb-10">
    <h2 class="text-2xl">Account Settings</h2>
  </div>
  <div>
    <div class="mb-4 grid grid-flow-col gap-4">
      <div>
        <Label for="firstName">First name</Label>
        <Input
          id="firstName"
          aria-label="First name"
          name="first_name"
          type="text"
          required
          placeholder="First name"
          v-model="form.first_name"
        />
      </div>
      <div>
        <Label for="lastName">Last name</Label>
        <Input
          id="lastName"
          aria-label="Last name"
          name="last_name"
          type="text"
          required
          placeholder="Last name"
          v-model="form.last_name"
        />
      </div>
    </div>
    <div class="mb-4">
      <Label for="email">Email</Label>
      <Input
        name="email"
        type="text"
        disabled
        placeholder="Email"
        v-model="form.email"
      />
    </div>
    <div class="mb-8 grid grid-flow-col gap-4">
      <div>
        <Label for="type">Business type</Label>
        <Select
          placeholder="Choose type"
          :options="userTypes"
          v-model="form.type"
        />
      </div>
      <div>
        <Label for="value">Business name</Label>
        <Input
          id="value"
          aria-label="Business name"
          name="value"
          type="text"
          required
          placeholder="Business name"
          v-model="form.value"
        />
      </div>
    </div>
    <div class="flex justify-end">
      <Button class="px-12 bg-indigo-600 hover:bg-indigo-500 text-white" @click="updateUser">Save</Button>
    </div>
  </div>
  <div class="mt-10 mb-10">
    <h2 class="text-2xl">Change Password</h2>
  </div>
  <div class="pb-20">
    <div class="grid lg:grid-cols-3 gap-4 mb-8">
      <div>
        <Label for="oldPassword">Old Password</Label>
        <Input
          id="oldPassword"
          aria-label="Old Password"
          name="old_password"
          type="password"
          required
          placeholder="**********"
          v-model="old_password"
          @change="handleInput('old_password')"
        />
      </div>
      <div>
        <Label for="newPassword">New Password</Label>
        <Input
          id="newPassword"
          aria-label="New Password"
          name="new_password"
          type="password"
          required
          placeholder="**********"
          v-model="new_password"
          @change="handleInput('new_password')"
        />
        <p class="error">{{ errors["new_password"] }}</p>
      </div>
      <div>
        <Label for="retypeNewPassword">Confirm New Password</Label>
        <Input
          id="retypeNewPassword"
          aria-label="Confirm New Password"
          name="retype_new_password"
          type="password"
          required
          placeholder="**********"
          v-model="retype_new_password"
          @change="handleInput('retype_new_password')"
        />
        <p class="error">{{ errors["retype_new_password"] }}</p>
      </div>
    </div>
    <div class="flex justify-end">
      <Button class="px-12 bg-indigo-600 hover:bg-indigo-500 text-white" @click="changePassword">Change Password</Button>
    </div>
  </div>
  <div class="w-full fixed top-0 flex justify-center pt-2 px-2" v-show="error">
    <div
      class="max-w-xl flex items-center bg-red-500 py-2 px-4 text-white rounded-md"
    >
      <div class="bg-red-600 p-1 mr-4 rounded-md">
        <Icon name="Close" class="w-5 h-5" />
      </div>
      <p class="text-left font-medium leading-tight">{{ error }}</p>
    </div>
  </div>
  <div
    class="w-full fixed top-0 flex justify-center pt-2 px-2"
    v-show="success"
  >
    <div
      class="max-w-xl flex items-center bg-green-500 py-2 px-4 text-white rounded-md"
    >
      <div class="bg-green-600 p-1 mr-4 rounded-md">
        <Icon name="Check" class="w-5 h-5" />
      </div>
      <p class="text-left font-medium leading-tight">{{ success }}</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "vue";
import { useStore } from "vuex";
import Input from "@/components/UI/Input";
import Label from "@/components/UI/Label";
import Button from "@/components/UI/Button";
import Select from "@/components/UI/Select";
import Icon from "@/components/UI/Icon";

export default {
  components: { Input, Label, Button, Select, Icon },
  setup() {
    const userTypes = [
      { label: "Agency", value: "agency" },
      { label: "Freelancer", value: "freelancer" },
      { label: "Ecommerce", value: "ecommerce" },
      { label: "Other", value: "other" },
    ];
    const store = useStore();

    const errors = ref({});
    const error = ref(null);
    const success = ref(null);

    const old_password = ref("");
    const new_password = ref(null);
    const retype_new_password = ref(null);

    const form = computed({
      get() {
        return store.state.user;
      },
      set(newValue) {
        store.commit("SET_USER", newValue);
      },
    });

    const updateUser = async () => {
      const { success, data } = await store.dispatch("UPDATE_USER");
      if (success) setSuccess("Updated user successfully!");
      else setError(data.data.message || data.data);
    };

    const changePassword = async () => {
      if (!new_password.value)
        errors.value.new_password = "Please enter a password";
     
      if (new_password.value !== retype_new_password.value)
        errors.value.retype_new_password = "Passwords must match";
      if (Object.keys(errors.value).length !== 0) {
        return true;
      }

      const { success, data } = await store.dispatch("CHANGE_PASSWORD", {
        old_password: old_password.value,
        new_password: new_password.value,
      });
      if (success) {
        setSuccess("Changed password successfully");
        old_password.value = "";
        new_password.value = null;
        retype_new_password.value = null;
      } else {
        setError(data.data.message);
      }
    };

    const handleInput = (input) => {
      delete errors.value[input];
    };

    const setSuccess = (data) => {
      success.value = data;
      setTimeout(() => {
        success.value = null;
      }, 4000);
    };

    const setError = (data) => {
      error.value = data;
      setTimeout(() => {
        error.value = null;
      }, 4000);
    };

    return {
      form,
      userTypes,
      errors,
      error,
      success,
      old_password,
      new_password,
      retype_new_password,
      updateUser,
      changePassword,
      handleInput,
      setSuccess,
      setError,
    };
  },
};
</script>

<style scoped>
.error {
  @apply font-medium text-red-600 mt-1;
}
</style>
